import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@tanstack/react-query";

import { Button, CustomSelect, Error, Label } from "../..";
import { postAxios } from "../../../Services/commonService";
import { MarketSchema } from "../../../Schema/Game/MarketSchema";
import { API_MARKET } from "../../../../utils/api/ApiConstant";
import { useFilteredData } from "../../../Hooks";
import { lowerFirstLetterUsingRegex } from "../../../helper/common";
import {
  sanitizeNameInput,
  sanitizePositiveNumberInput,
} from "../../../../utils/inputUtils";

const MarketModal = ({ handleModalHide, rowData, fetchData }) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(MarketSchema),
    defaultValues: {
      id: "",
      eventTypeId: null,
      competitionId: null,
      eventId: null,
      bettingTypeId: null,
      marketTypeId: null,
      displayOrder: "",
      name: "",
    },
  });

  const selectedSport = watch("eventTypeId");
  const selectedTournament = watch("competitionId");
  const selectedMatch = watch("eventId");
  const selectedBettingType = watch("bettingTypeId");

  const onSubmit = async (values) => {
    const body = {
      id: values?.id,
      eventTypeId: values?.eventTypeId.value,
      competitionId: values?.competitionId.value,
      eventId: values?.eventId.value,
      name: values?.name,
      marketTypeId: values?.marketTypeId.value,
      bettingTypeId: values?.bettingTypeId.value,
      displayOrder: Number(values?.displayOrder),
    };

    const res = await postAxios(API_MARKET, body);

    if (res?.success) {
      fetchData();
      handleModalHide();
    }
    res?.errors?.forEach((element) => {
      const field = lowerFirstLetterUsingRegex(element?.field);
      if (field)
        setError(field, {
          type: "manual",
          message: element.message,
        });
    });
  };

  const {
    sportOptions,
    tournamentOptions,
    matchOptions,
    bettingTypeOptions,
    marketTypeOptions,
  } = useFilteredData({
    sport: selectedSport?.value,
    tournament: selectedTournament?.value,
    match: selectedMatch?.value,
    bettingType: selectedBettingType?.value,
    dependantMatch: true,
    enableBettingType: true,
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <fieldset disabled={isSubmitting}>
        <div className="row">
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label htmlFor="id" className="form-label" isRequired={true}>
                Id
              </Label>
              <input
                type="text"
                className={`form-control ${errors.id ? "is-invalid" : ""}`}
                id="id"
                placeholder="Enter Id"
                autoFocus={true}
                {...register("id", {
                  onChange: (e) => {
                    e.target.value = sanitizePositiveNumberInput(
                      e.target.value,
                    );
                  },
                })}
                tabIndex={1}
              />
              {errors.id && <Error>{errors.id.message}</Error>}
            </div>
          </div>
          {/* SPORTS */}
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="eventTypeId"
                className="form-label"
                isRequired={true}
              >
                Sports
              </Label>

              <Controller
                name="eventTypeId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    options={sportOptions}
                    placeholder="Select Sports"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption);
                      setValue("competitionId", null);
                      setValue("eventId", null);
                      setValue("bettingTypeId", null);
                      setValue("marketTypeId", null);
                    }}
                    tabIndex={2}
                  />
                )}
              />
              {errors.eventTypeId && (
                <Error>{errors.eventTypeId.message}</Error>
              )}
            </div>
          </div>

          {/* TOURNAMENT */}
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="competitionId"
                className="form-label"
                isRequired={true}
              >
                Tournament
              </Label>

              <Controller
                name="competitionId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    options={tournamentOptions}
                    placeholder="Select Tournament"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption);
                      setValue("eventId", null);
                    }}
                    tabIndex={3}
                  />
                )}
              />

              {errors.competitionId && (
                <Error>{errors.competitionId.message}</Error>
              )}
            </div>
          </div>

          {/* MATCH */}
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label htmlFor="eventId" className="form-label" isRequired={true}>
                Matches
              </Label>

              <Controller
                name="eventId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    options={matchOptions}
                    placeholder="Select Match"
                    isMulti={false}
                    tabIndex={4}
                  />
                )}
              />
              {errors.eventId && <Error>{errors.eventId.message}</Error>}
            </div>
          </div>
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="bettingTypeId"
                className="form-label"
                isRequired={true}
              >
                Betting Type
              </Label>

              <Controller
                name="bettingTypeId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    options={bettingTypeOptions}
                    placeholder="Select Match"
                    isMulti={false}
                    onChange={(selectedOption) => {
                      field.onChange(selectedOption);
                      setValue("marketTypeId", null);
                    }}
                    tabIndex={5}
                  />
                )}
              />
              {errors.bettingTypeId && (
                <Error>{errors.bettingTypeId.message}</Error>
              )}
            </div>
          </div>

          {/* MARKET TYPE */}
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="marketTypeId"
                className="form-label"
                isRequired={true}
              >
                Market Type
              </Label>

              <Controller
                name="marketTypeId"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    {...field}
                    options={marketTypeOptions}
                    placeholder="Select Market"
                    isMulti={false}
                    tabIndex={6}
                  />
                )}
              />
              {errors.marketTypeId && (
                <Error>{errors.marketTypeId.message}</Error>
              )}
            </div>
          </div>
          <div className="col-lg-6 col-12">
            <div className="form-group">
              <Label htmlFor="name" className="form-label" isRequired={true}>
                Market Name
              </Label>
              <input
                type="text"
                className="form-control"
                id="name"
                placeholder="Enter name"
                {...register("name", {
                  onChange: (e) => {
                    e.target.value = sanitizeNameInput(e.target.value);
                  },
                })}
                tabIndex={7}
              />
              {errors.name && <Error>{errors.name.message}</Error>}
            </div>
          </div>
          <div className="col-md-6 col-12">
            <div className="form-group">
              <Label
                htmlFor="displayOrder"
                className="form-label"
                isRequired={true}
              >
                Display Order
              </Label>
              <input
                type="text"
                className={`form-control ${errors.displayOrder ? "is-invalid" : ""}`}
                id="displayOrder"
                placeholder="Enter Display Order"
                {...register("displayOrder", {
                  onChange: (e) => {
                    e.target.value = sanitizePositiveNumberInput(
                      e.target.value,
                    );
                  },
                })}
                tabIndex={8}
              />
              {errors.displayOrder && (
                <Error>{errors.displayOrder.message}</Error>
              )}
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end w-100 mt-2">
          <Button
            type="button"
            className="btn btn_main btn_muted px-3 py-2 mx-1"
            onClick={handleModalHide}
            tabIndex={10}
          >
            Close
          </Button>
          <Button
            type="submit"
            className="btn btn_main px-3 py-2 mx-1"
            tabIndex={9}
            isDisabled={isSubmitting}
          >
            {isSubmitting ? "Loading.." : "Submit"}
          </Button>
        </div>
      </fieldset>
    </form>
  );
};

export default MarketModal;
